import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { ApiContext } from "../../api-context";
import { Modal } from "../../components/modal";
import { ToastContext } from "../../components/toast";
import { withResponse } from "../../utils/with-response";

import type { Institute, InstitutesResponse } from "@sperrdatei/dto/types";

export const ListInstitutes: React.FC = () => {
  const navigate = useNavigate();
  const toast = useContext(ToastContext);
  const [institutes, setInstitutes] = useState<InstitutesResponse[200]>([]);
  const [visibleInstitutes, setVisibleInstitutes] = useState<
    InstitutesResponse[200]
  >([]);
  const [selectedInstitute, setSelectedInstitute] = useState<
    Institute | undefined
  >(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { apiFetch } = useContext(ApiContext);

  const loadInstitutes = useCallback(async (): Promise<void> => {
    const response = await apiFetch("/api/admin/institute");

    if (response.ok) {
      response.json().then((result: InstitutesResponse[200]) => {
        setInstitutes(result);
        setVisibleInstitutes(result);
      });
    } else {
      navigate("/error");
    }
  }, [apiFetch, navigate]);

  useEffect(() => {
    loadInstitutes();
  }, [loadInstitutes]);

  const filterInstitutes = (value: string): void => {
    setVisibleInstitutes(
      institutes.filter((institute) => {
        const matchInName =
          institute.name.toLowerCase().indexOf(value.toLowerCase()) >= 0;

        const matchInDomains = institute.domains.find(
          (d) => d.domainName.toLowerCase().indexOf(value) >= 0,
        );

        const matchInAdmins = institute.users.find(
          (u) =>
            u.username.toLowerCase().indexOf(value) >= 0 &&
            u.roles.some(
              (role) => role === "ADMIN" || role === "INSTITUTE_ADMIN",
            ),
        );

        return matchInName || matchInDomains || matchInAdmins;
      }),
    );
  };

  return (
    <div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Institut Löschen"
        primaryAction={{
          label: "Löschen",
          buttonStyle: "danger",
          onClick: async () => {
            if (selectedInstitute) {
              const response = await apiFetch(
                `/api/admin/institute/${selectedInstitute.id}`,
                {
                  method: "DELETE",
                },
              );
              await withResponse(response, {
                onSuccess: async () => {
                  await loadInstitutes();
                  setIsModalOpen(false);
                  toast?.showToast({
                    caption: "Institut gelöscht",
                    message: "Das Institut wurde gelöscht",
                  });
                },
                onFailure: async (error) => {
                  return error;
                },
              });
            }
          },
        }}
        secondaryAction={{
          label: "Abbrechen",
          onClick: () => setIsModalOpen(false),
        }}
      >
        <p>
          Sind Sie sicher das sie das Institut <b>{selectedInstitute?.name}</b>{" "}
          und alle Benutzer löschen möchten?
        </p>
      </Modal>

      <h3 className="page-caption mb-3">Institute</h3>

      <div className="input-group mb-3 w-25">
        <span className="input-group-text" id="basic-addon1">
          Filter
        </span>
        <input
          type="text"
          className="form-control"
          placeholder="Name, Admin, Domain"
          onChange={(e) => {
            filterInstitutes(e.target.value);
          }}
        />
      </div>

      <table className="table">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Name</th>
            <th scope="col">Admin</th>
            <th scope="col">Domains</th>
            <th scope="col">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {visibleInstitutes.map((institute, idx) => (
            <tr key={institute.id}>
              <th scope="row">{idx}</th>
              <td>{institute.name}</td>
              <td>
                {institute.users
                  .filter((user) =>
                    user.roles.some(
                      (role) => role === "ADMIN" || role === "INSTITUTE_ADMIN",
                    ),
                  )
                  .map((user) => `${user.username}@${user.domain}`)
                  .join(", ")}
              </td>
              <td>
                {institute.domains
                  .map((domain) => domain.domainName)
                  .join("; ")}
              </td>
              <td className="actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  title="Bearbeiten"
                  onClick={() => {
                    navigate(
                      `/administration/institutes/edit?institutId=${institute.id}`,
                    );
                  }}
                >
                  <i className="bi bi-pencil-square"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-danger ml-1"
                  title="Löschen"
                  onClick={() => {
                    setIsModalOpen(true);
                    setSelectedInstitute(institute);
                  }}
                >
                  <i className="bi bi-trash3"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
